import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useBranchesData from '../../hooks/useBranchesData';
import { Tabs } from '../ui';
import { fromLocation, unitPath } from '../../utils/unitRoute';
import SOURCE_LABELS from '../../config/sourceLabels';
import { findUnlistedUnits } from '../../utils/listingCoverage';
import BranchesOverviewV2 from './BranchesOverviewV2';
import CategoryTrendsV2 from './CategoryTrendsV2';
import ListingCoverageV2 from './ListingCoverageV2';
import '../ui/ui.css';
import './BranchesV2.css';

// Tab ids as they appear in the address (#trends, #coverage; Branches is the
// default and has no hash) and the data view each one loads.
export const BRANCH_TABS = ['branches', 'trends', 'coverage'];
const DATA_VIEW = { branches: 'overview', trends: 'category', coverage: 'coverage' };

export const tabFromHash = (hash) => {
    const id = String(hash || '').replace(/^#/, '');
    return BRANCH_TABS.includes(id) ? id : 'branches';
};

const formatToday = (date) => date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * The redesigned Branches page (shown with the "new design" flag on). Data
 * comes through hooks/useBranchesData (shared with today's page): the
 * inventory on mount, each tab's payload the first time it is shown. The tab
 * is kept in the address hash so a reload or a shared link reopens it.
 * The dealership, the Read-only badge and the user menu are the v2 top bar's.
 */
export default function BranchesPageV2() {
    const location = useLocation();
    const navigate = useNavigate();
    const tab = tabFromHash(location.hash);
    const data = useBranchesData(DATA_VIEW[tab]);
    const [today] = useState(() => new Date());

    // Listing Coverage's "#stock" opens the unit's full page (U1); Back comes
    // back here, to the same tab (the hash is part of the address).
    const openUnit = useCallback((unit) => {
        const stock = String(unit.StockNumber);
        navigate(unitPath(stock, 'ai'), { state: { from: fromLocation(location), stocks: [stock] } });
    }, [navigate, location]);

    // The Branches tab's ?branches= / ?compare= stay in the address while
    // another tab is open (those tabs ignore them), so coming back, or Back /
    // Forward, restores the selection and the basis.
    const changeTab = (id) => {
        navigate(
            { pathname: location.pathname, search: location.search, hash: id === 'branches' ? '' : `#${id}` },
            { replace: true, state: location.state },
        );
    };

    // Coverage badge: only once both payloads are in (the matrix still loads
    // lazily, on the tab's first visit).
    const unlistedCount = useMemo(() => (
        data.matrixData && data.invUnits.length && !data.matrixError
            ? findUnlistedUnits(data.invUnits, data.matrixData.rows).length
            : null
    ), [data.matrixData, data.invUnits, data.matrixError]);

    const combined = data.combinedData;
    const branchCount = combined?.branches?.length;

    const panels = {
        branches: <BranchesOverviewV2 data={data} />,
        trends: <CategoryTrendsV2 data={data} />,
        coverage: <ListingCoverageV2 data={data} onOpenUnit={openUnit} />,
    };

    return (
        <>
            <div className="br2" data-testid="branches-v2">
                <main className="br2-page">
                    <div className="br2-ph">
                        <h1>Branches</h1>
                        <span className="br2-meta">
                            {branchCount ? (
                                <>
                                    <b>{branchCount.toLocaleString('en-US')}</b> branches · <b>{(combined.total_units ?? 0).toLocaleString('en-US')}</b> units ·{' '}
                                </>
                            ) : null}
                            {formatToday(today)}
                        </span>
                    </div>
                    <Tabs
                        label="Branches views"
                        className="br2-tabs"
                        value={tab}
                        onChange={changeTab}
                        tabs={[
                            { id: 'branches', label: 'Branches', panel: panels.branches },
                            { id: 'trends', label: 'Category Trends', panel: panels.trends },
                            { id: 'coverage', label: SOURCE_LABELS.listingCoverageGap, count: unlistedCount, panel: panels.coverage },
                        ].map((t) => (t.id === tab ? t : { ...t, panel: undefined }))}
                    />
                </main>
                <footer className="br2-appfoot">
                    <span>&copy; 2026 Fusable. All rights reserved.</span>
                </footer>
            </div>
        </>
    );
}
